import "./styles/App.css";

import React, { useState, useEffect } from "react";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import Display from "./components/display";
import StatusBar from "./components/statusbar";
import Login from "./screens/login";
import LoadingScreen from "./components/LoadingScreen";
import { UserPreferencesProvider } from "./contexts/UserPreferencesContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import OnboardingWizard from "./screens/onboarding";
import PaywallGate from "./components/PaywallGate";
import { MenuPreferencesProvider } from "./contexts/MenuPreferencesContext";
import { usePersistedState } from "./hooks/usePersistedState";
import TaskNotificationManager from "./components/TaskNotificationManager";
import CertificateModal from "./screens/certificates/CertificateModal";
import CertificateExpirationAlert from "./components/CertificateExpirationAlert";
import XmlDownloadModal from "./components/XmlDownloadModal";
import XmlDownloadTest from "./components/XmlDownloadTest";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import AutomacaoAutomec from "./screens/automacao-automec";
import AutomecQuestionario from "./screens/automec-questionario";
import ReformaSYL from "./screens/reforma-syl";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import TermsOfService from "./screens/legal/TermsOfService";
import PrivacyPolicy from "./screens/legal/PrivacyPolicy";
import InviteAccept from "./screens/invite";
import DemoEntry from "./screens/demo";
import DemoBanner from "./components/DemoBanner";
import DemoWelcome from "./screens/demo-welcome";
import DemoTour from "./components/DemoTour";
import { isDemoMode, hasDemoWelcomeBeenShown, markDemoWelcomeShown, resetDemoWelcome } from "./services/demoService";
import { lightTheme, darkTheme } from "./styles/themes";

// Temas (lightTheme / darkTheme) movidos para ./styles/themes.js

// Estilos globais — Inter font, scrollbar LP, selection
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  body {
    background: ${({ theme }) => theme.body};
    color: ${({ theme }) => theme.text};
    font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  ::selection {
    background: rgba(16, 185, 129, 0.25);
  }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'};
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.14)'};
  }
  /* Hide autofill background styling */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.body} inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.text} !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

// Container principal
const AppContainer = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "linear-gradient(180deg, #f0f2f5 0%, #fafbfc 30%, #ffffff 100%)"
      : "linear-gradient(135deg, #0d0f14 0%, #12141a 50%, #0d0f14 100%)"};
  transition: background 0.3s ease;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? `radial-gradient(ellipse 80% 50% at 50% -20%, rgba(16, 185, 129, 0.06), transparent),
           radial-gradient(ellipse 60% 40% at 100% 0%, rgba(59, 130, 246, 0.03), transparent)`
        : "none"};
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? `linear-gradient(rgba(0, 0, 0, 0.015) 1px, transparent 1px),
           linear-gradient(90deg, rgba(0, 0, 0, 0.015) 1px, transparent 1px)`
        : "none"};
    background-size: 40px 40px;
    pointer-events: none;
    z-index: 0;
  }

  .content-app {
    width: 100%;
    height: 100%;
    display: flex;
    position: relative;
    z-index: 1;
  }
`;

function AppContent() {
  const { isAuthenticated, loading, user } = useAuth();
  const [electron, setElectron] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDemoWelcome, setShowDemoWelcome] = useState(false);
  const [tourActive, setTourActive] = useState(false);

  const [menuMinimized, setMenuMinimized] = usePersistedState(
    "vidal_sidebar_minimized",
    false
  );
  const [isDark, setIsDark] = usePersistedState("vidal_theme", false);
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [isXmlDownloadModalOpen, setIsXmlDownloadModalOpen] = useState(false);

  // No modo demo, todo carregamento/reload (Ctrl+R) reinicia o tutorial do começo
  useEffect(() => {
    if (isDemoMode()) {
      resetDemoWelcome();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user && user.cargo !== 'god') {
      // Nunca mostrar onboarding no modo demonstração
      if (isDemoMode() || user.isDemo) {
        setShowOnboarding(false);
        return;
      }
      // Nunca mostrar onboarding para usuários convidados
      if (user.invitedAt) {
        setShowOnboarding(false);
        return;
      }
      const alreadyDone = localStorage.getItem(`vidal_onboarding_done_${user?.id}`);
      if (alreadyDone) {
        setShowOnboarding(false);
        return;
      }
      // Mostrar welcome tour apenas para usuários auto-registrados
      setShowOnboarding(true);
    }
  }, [isAuthenticated, user]);

  // Mostrar tela de boas-vindas no modo demo (sempre como usuário novo)
  useEffect(() => {
    if (isAuthenticated && (isDemoMode() || user?.isDemo)) {
      if (!hasDemoWelcomeBeenShown()) {
        setShowDemoWelcome(true);
      }
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const isElectronApp = navigator.userAgent
      .toLocaleLowerCase()
      .includes("electron");
    setElectron(isElectronApp);
  }, []);

  // Disable autocomplete on all inputs except login
  useEffect(() => {
    if (!isAuthenticated) return;
    const observer = new MutationObserver(() => {
      document.querySelectorAll('input, textarea').forEach(el => {
        if (!el.closest('[data-login]') && !el.getAttribute('data-keep-autocomplete')) {
          if (!el.hasAttribute('autocomplete') || el.getAttribute('autocomplete') !== 'off') {
            el.setAttribute('autocomplete', 'off');
          }
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isAuthenticated]);

  // Listen for open-certificate-modal events from child screens
  useEffect(() => {
    const handler = () => setIsCertificateModalOpen(true);
    window.addEventListener('open-certificate-modal', handler);
    return () => window.removeEventListener('open-certificate-modal', handler);
  }, []);

  const handleMenuToggle = () => {
    setMenuMinimized(!menuMinimized);
  };

  const handleThemeToggle = () => {
    setIsDark(!isDark);
  };

  const currentTheme = isDark ? darkTheme : lightTheme;

  if (loading) {
    return (
      <ThemeProvider theme={currentTheme}>
        <GlobalStyle />
        <LoadingScreen isDark={isDark} />
      </ThemeProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={currentTheme}>
        <GlobalStyle />
        <Login />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={currentTheme}>
      <UserPreferencesProvider>
        <MenuPreferencesProvider>
          <GlobalStyle />
          {(isDemoMode() || user?.isDemo) && <DemoBanner />}
          <StatusBar isDark={isDark} onToggleTheme={handleThemeToggle} />
          <AppContainer id="app">
            <div className="content-app">
              <Display
                menuMinimized={menuMinimized}
                electron={electron}
                onMenuToggle={handleMenuToggle}
                isDark={isDark}
                onOpenCertificateModal={() => setIsCertificateModalOpen(true)}
                onOpenXmlDownloadModal={() => setIsXmlDownloadModalOpen(true)}
              />
            </div>
            <TaskNotificationManager isDark={isDark} />
            <CertificateModal
              isOpen={isCertificateModalOpen}
              onClose={() => setIsCertificateModalOpen(false)}
            />
            <XmlDownloadModal
              isOpen={isXmlDownloadModalOpen}
              onClose={() => setIsXmlDownloadModalOpen(false)}
            />
            <CertificateExpirationAlert
              onOpenCertificateModal={() => setIsCertificateModalOpen(true)}
              demoTutorialActive={(isDemoMode() || user?.isDemo) && (showDemoWelcome || tourActive)}
            />
            {showOnboarding && (
              <OnboardingWizard
                onComplete={() => {
                  setShowOnboarding(false);
                  localStorage.setItem(`vidal_onboarding_done_${user?.id}`, 'true');
                }}
              />
            )}
            {showDemoWelcome && (
              <DemoWelcome
                theme={currentTheme}
                onStartTutorial={() => {
                  setShowDemoWelcome(false);
                  markDemoWelcomeShown();
                  setTourActive(true);
                }}
                onSkip={() => {
                  setShowDemoWelcome(false);
                  markDemoWelcomeShown();
                }}
              />
            )}
            {tourActive && (
              <DemoTour onFinish={() => setTourActive(false)} />
            )}
            {/* Paywall obrigatório: bloqueia o app até o pagamento ser confirmado */}
            <PaywallGate />
          </AppContainer>
          <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDark ? "dark" : "light"}
          style={{ zIndex: 99999 }}
          toastStyle={{
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            boxShadow: isDark 
              ? '0 8px 32px rgba(0, 0, 0, 0.4)' 
              : '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: isDark 
              ? '1px solid rgba(255, 255, 255, 0.1)' 
              : '1px solid rgba(0, 0, 0, 0.08)',
            fontFamily: '"Inter", system-ui, sans-serif',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
        />
        </MenuPreferencesProvider>
      </UserPreferencesProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/termos" element={<TermsOfService />} />
          <Route path="/privacidade" element={<PrivacyPolicy />} />
          <Route path="/convite" element={<InviteAccept />} />
          <Route path="/demo" element={<DemoEntry />} />
          <Route path="/automacao-automec" element={<AutomacaoAutomec />} />
          <Route path="/automec" element={<AutomecQuestionario />} />
          <Route 
            path="/reforma-syl" 
            element={
              <ProtectedRoute requiredEmail="contabilidade@syl.com.br">
                <ReformaSYL />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/proposta-syl" 
            element={
              <ProtectedRoute requiredEmail="contabilidade@syl.com.br">
                <ReformaSYL />
              </ProtectedRoute>
            } 
          />
          <Route path="*" element={<AppContent />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
